import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAppController, setMiniSidenav } from 'context';
import { cn } from 'components/shadcn/lib/utils';
import { Card } from 'components/shadcn/ui/card';
import HGOptions from 'components/TopNav/HGOptions';

function Sidenav({ brand, brandMini, routes }) {
    const [controller, dispatch] = useAppController();
    const { miniSidenav } = controller;
    const { pathname } = useLocation();
    const [openGroups, setOpenGroups] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1280);

    useEffect(() => {
        function handleResize() {
            setIsMobile(window.innerWidth < 1280);
            setMiniSidenav(dispatch, window.innerWidth < 1200);
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [dispatch]);

    const toggleGroup = (key) =>
        setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    const renderRoute = (route) => {
        if (route.hidden) return null;

        if (route.collapse) {
            const isGroupActive = route.collapse.some(
                (r) => pathname === r.route
            );

            // Mobile: show parent icon linking to first visible sub-route
            if (isMobile) {
                const firstSub = route.collapse.find((r) => !r.hidden);
                return (
                    <NavLink
                        key={route.key}
                        to={firstSub?.route || '#'}
                        className={cn(
                            'flex items-center justify-center rounded-lg p-2 text-sm transition-colors',
                            isGroupActive
                                ? 'bg-muted font-medium text-foreground'
                                : 'text-muted-foreground hover:bg-muted'
                        )}
                    >
                        {route.icon && (
                            <span className="h-5 w-5 shrink-0">{route.icon}</span>
                        )}
                    </NavLink>
                );
            }

            const isOpen = openGroups[route.key] ?? isGroupActive;

            return (
                <div key={route.key}>
                    <button
                        onClick={() => toggleGroup(route.key)}
                        className={cn(
                            'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors',
                            isGroupActive
                                ? 'bg-muted font-medium text-foreground'
                                : 'text-muted-foreground hover:bg-muted'
                        )}
                    >
                        {route.icon && (
                            <span className="h-5 w-5 shrink-0">
                                {route.icon}
                            </span>
                        )}
                        {!miniSidenav && (
                            <>
                                <span className="flex-1 text-left capitalize">
                                    {route.name}
                                </span>
                                <ChevronDownIcon
                                    className={cn(
                                        'h-4 w-4 transition-transform',
                                        isOpen && 'rotate-180'
                                    )}
                                />
                            </>
                        )}
                    </button>
                    {isOpen && !miniSidenav && (
                        <div className="ml-8 mt-1 space-y-1">
                            {route.collapse.map(
                                (sub) =>
                                    !sub.hidden && (
                                        <NavLink
                                            key={sub.key}
                                            to={sub.route}
                                            className={({ isActive }) =>
                                                cn(
                                                    'flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm capitalize transition-colors',
                                                    isActive
                                                        ? 'bg-muted font-medium text-foreground'
                                                        : 'text-muted-foreground hover:bg-muted'
                                                )
                                            }
                                        >
                                            {sub.icon && (
                                                <span className="h-4 w-4 shrink-0">
                                                    {sub.icon}
                                                </span>
                                            )}
                                            {sub.name}
                                        </NavLink>
                                    )
                            )}
                        </div>
                    )}
                </div>
            );
        }

        const isSitesDefault =
            route.key === 'sites' && pathname === '/';

        // Mobile: icon-only horizontal item
        if (isMobile) {
            return (
                <NavLink
                    key={route.key}
                    to={route.route}
                    className={({ isActive }) =>
                        cn(
                            'flex items-center justify-center rounded-lg p-2 text-sm transition-colors',
                            isActive || isSitesDefault
                                ? 'bg-muted font-medium text-foreground'
                                : 'text-muted-foreground hover:bg-muted'
                        )
                    }
                >
                    {route.icon && (
                        <span className="h-5 w-5 shrink-0">{route.icon}</span>
                    )}
                </NavLink>
            );
        }

        return (
            <NavLink
                key={route.key}
                to={route.route}
                className={({ isActive }) =>
                    cn(
                        'flex items-center gap-3 rounded-lg px-3 py-3 text-sm capitalize transition-colors',
                        isActive || isSitesDefault
                            ? 'bg-muted font-medium text-foreground'
                            : 'text-muted-foreground hover:bg-muted'
                    )
                }
            >
                {route.icon && (
                    <span className="h-5 w-5 shrink-0">{route.icon}</span>
                )}
                {!miniSidenav && <span>{route.name}</span>}
            </NavLink>
        );
    };

    return (
        <Card
            className={cn(
                'fixed z-50 flex rounded-2xl transition-all duration-300',
                // Mobile: horizontal bar across the top
                'left-2 right-2 top-2 h-14 flex-row items-center',
                // Desktop: vertical sidebar on the left
                'xl:right-auto xl:h-[calc(100%-1rem)] xl:flex-col xl:items-stretch',
                miniSidenav ? 'xl:w-16' : 'xl:w-44'
            )}
        >
            <div className={cn(
                'flex shrink-0 items-center justify-center px-4',
                'h-14 xl:h-16'
            )}>
                <img
                    src={isMobile ? brandMini : (miniSidenav ? brandMini : brand)}
                    alt="logo"
                    className="h-8 object-contain"
                />
            </div>
            <nav
                className={cn(
                    'flex flex-1',
                    // Mobile: horizontal scrollable row
                    'flex-row gap-1 overflow-x-auto p-2',
                    // Desktop: vertical column
                    'xl:flex-col xl:overflow-x-hidden xl:overflow-y-auto xl:space-y-1',
                    miniSidenav ? 'xl:p-2' : 'xl:p-3'
                )}
            >
                {routes.map(renderRoute)}
            </nav>
            <div className={cn('shrink-0 p-2', miniSidenav ? 'xl:p-2' : 'xl:p-3')}>
                <HGOptions />
            </div>
        </Card>
    );
}

export default Sidenav;
